import React, {useContext, useEffect, useState} from "react";
import Header from "../../../components/header/header";
import SettingsSubmenu from "../components/submenu/settings_submenu";
import SettingsSection from "../components/section/section";
import styles from "../styles/settings.module.css";
import axiosInstance from "../../../AxiosInstance";
import Modal from "../../../components/modal/modal";
import {SelectInput, TextInput} from "../../../components/input/inputs";
import MyContext from "../../../Context";
import LimitExceededModal from "../../../components/limit_exceeded_modal/LimitExceededModal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";

function CompanyOfficeSelector({companies, selectedProjectIds, setSelectedProjectIds}) {
    const {t} = useTranslation();
    const [expandedIds, setExpandedIds] = useState(
        companies.length === 1 ? (companies[0] ? [companies[0].id] : []) : []
    );

    const toggleExpand = (id) => {
        setExpandedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleProject = (id) => {
        setSelectedProjectIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleAllCompany = (company) => {
        const ids = (company.projects || []).map(p => p.id);
        const allSelected = ids.length > 0 && ids.every(id => selectedProjectIds.includes(id));
        if (allSelected) {
            setSelectedProjectIds(prev => prev.filter(id => !ids.includes(id)));
        } else {
            setSelectedProjectIds(prev => [...new Set([...prev, ...ids])]);
        }
    };

    return (
        <div style={{border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden'}}>
            {companies.map((company, idx) => {
                const projects = company.projects || [];
                const selectedCount = projects.filter(p => selectedProjectIds.includes(p.id)).length;
                const allSelected = projects.length > 0 && selectedCount === projects.length;
                const isExpanded = expandedIds.includes(company.id);
                return (
                    <div key={company.id} style={{borderBottom: idx < companies.length - 1 ? '1px solid #E2E8F0' : 'none'}}>
                        <div
                            style={{display: 'flex', alignItems: 'center', padding: '10px 12px', background: '#F8FAFC', gap: '8px', cursor: 'pointer', userSelect: 'none'}}
                            onClick={() => toggleExpand(company.id)}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} color={'#94A3B8'} style={{width: '10px', flexShrink: 0}}/>
                            <span style={{flex: 1, color: '#334155', fontWeight: '600', fontSize: '13px'}}>{company.name}</span>
                            {selectedCount > 0 && (
                                <span style={{fontSize: '11px', color: '#4F46E5', fontWeight: '600'}}>
                                    {allSelected ? t('settings.users.allSelected') : t('settings.users.countSelected', {count: selectedCount})}
                                </span>
                            )}
                            <span
                                style={{fontSize: '11px', color: allSelected ? '#4F46E5' : '#64748B', fontWeight: '500', padding: '2px 8px', borderRadius: '4px', border: '1px solid', borderColor: allSelected ? '#C7D2FE' : '#E2E8F0', background: 'white'}}
                                onClick={(e) => { e.stopPropagation(); toggleAllCompany(company); }}
                            >
                                {allSelected ? t('settings.users.none') : t('settings.users.all')}
                            </span>
                        </div>
                        {isExpanded && (
                            <div>
                                {projects.length === 0
                                    ? <div style={{padding: '8px 28px', color: '#94A3B8', fontSize: '12px'}}>{t('settings.users.noOffices')}</div>
                                    : projects.map(p => {
                                        const checked = selectedProjectIds.includes(p.id);
                                        return (
                                            <label key={p.id} style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px 8px 28px', cursor: 'pointer', fontSize: '13px', color: checked ? '#4F46E5' : '#475569', fontWeight: checked ? '500' : '400', background: checked ? '#EEF2FF' : 'white', borderTop: '1px solid #F1F5F9'}}>
                                                <input
                                                    type={'checkbox'}
                                                    checked={checked}
                                                    onChange={() => toggleProject(p.id)}
                                                    style={{accentColor: '#4F46E5', width: '14px', height: '14px'}}
                                                />
                                                {p.name}
                                            </label>
                                        );
                                    })
                                }
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function UserSettings() {
    const {t} = useTranslation();
    const [selectedUser, setSelectedUser] = useState({});
    const [users, setUsers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [roles, setRolees] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [inviteError, setInviteError] = useState('');
    const {corporateGroup} = useContext(MyContext);
    const [form, setForm] = useState({});
    const [availableCompanies, setAvailableCompanies] = useState([]);
    const [selectedProjectIds, setSelectedProjectIds] = useState([]);
    const [editProjectIds, setEditProjectIds] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Limit exceeded modal state
    const [userForm, setUserForm] = useState({});

    // Limit exceeded modal state
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [limitError, setLimitError] = useState({error: '', current: 0, max: 0, code: '', subscriptionStatus: null});

    // Derive allowed_company_ids from selected project IDs (auto-computed)
    const getCompanyIds = (projectIds) => [...new Set(
        availableCompanies
            .filter(c => (c.projects || []).some(p => projectIds.includes(p.id)))
            .map(c => c.id)
    )];

    useEffect(() => {
        setForm({
            ...form,
            corporate_group_id: corporateGroup.id
        });
        axiosInstance.get("user/").then((res) => {
            setUsers(res.data);
        });

        axiosInstance.get("user_invite/").then((res) => {
            setInvites(res.data);
        });

        axiosInstance.get("role/").then((res) => {
            setRolees(res.data.results);
        });

        axiosInstance.get("company/").then((res) => {
            setAvailableCompanies(res.status === 206 ? [res.data] : res.data);
        }).catch(() => {});
    }, []);

    const sendInvite = () => {
        setInviteError('');
        axiosInstance.post('user_invite/', {
            ...form,
            'corporate_group_id': corporateGroup.id,
            'allowed_company_ids': getCompanyIds(selectedProjectIds),
            'allowed_project_ids': selectedProjectIds,
        }).then(() => {
            window.location.reload();
        }).catch((err) => {
            // Handle 402 Payment Required - Limit exceeded
            if (err.response?.status === 402) {
                const data = err.response.data;
                setLimitError({
                    error: data.error || t('settings.users.limitExceeded'),
                    current: data.current || 0,
                    max: data.max || 0,
                    code: data.code || '',
                    subscriptionStatus: data.status || null
                });
                setShowModal(false);
                setShowLimitModal(true);
                return;
            }
            if (err.response?.status === 400 && err.response.data?.error === 'INVITE_ALREADY_PENDING') {
                setInviteError(t('settings.users.inviteAlreadyPending'));
                return;
            }
            console.error('Error inviting user:', err);
            setInviteError(t('settings.users.inviteError'));
        });
    };

    const cancelInvite = (inviteId) => {
        axiosInstance.delete(`user_invite/${inviteId}/`).then(() => {
            setInvites(invites.filter(i => i.id !== inviteId));
        });
    };

    const resendInvite = (inviteId) => {
        axiosInstance.put(`user_invite/${inviteId}/`).then(() => {
            setShowSuccessModal(true);
        });
    };

    const updateUser = () => {
        axiosInstance.put('user/1/', {
            user_id: selectedUser.id,
            username: userForm.username,
            first_name: userForm.first_name,
            last_name: userForm.last_name,
            role: userForm.role_mod,
            allowed_company_ids: getCompanyIds(editProjectIds),
            allowed_project_ids: editProjectIds,
        }).then(() => {
            window.location.reload();
        });
    };

    return (
        <div className={styles.dashboard_container}>
            <Header/>

            <LimitExceededModal
                show={showLimitModal}
                setter={setShowLimitModal}
                error={limitError.error}
                current={limitError.current}
                max={limitError.max}
                type="users"
                code={limitError.code}
                subscriptionStatus={limitError.subscriptionStatus}
            />

            <Modal show={showUserModal} setter={setShowUserModal} name={t('settings.users.updateUser')} buttonName={t('settings.users.update')} onClick={() => updateUser()}>
                <TextInput name={'username'} form={userForm} setForm={setUserForm}/>
                <TextInput name={'First Name'} internalName={'first_name'} form={userForm} setForm={setUserForm}/>
                <TextInput name={'Last Name'} internalName={'last_name'} form={userForm} setForm={setUserForm}/>
                <div className={'mb-3'}>
                    <SelectInput name={'role'} internalName={'role_mod'} form={userForm} setForm={setUserForm} options={roles}/>
                </div>

                {availableCompanies.length > 0 && (
                    <div className={'mb-3'}>
                        <div style={{color: '#334155', fontWeight: '600', fontSize: '13px', marginBottom: '4px'}}>{t('settings.users.officeAccess')}</div>
                        <div style={{color: '#64748B', fontSize: '12px', marginBottom: '8px'}}>{t('settings.users.officeAccessHint')}</div>
                        <CompanyOfficeSelector
                            companies={availableCompanies}
                            selectedProjectIds={editProjectIds}
                            setSelectedProjectIds={setEditProjectIds}
                        />
                    </div>
                )}
            </Modal>

            <Modal show={showSuccessModal} setter={setShowSuccessModal} name={'Invitation Resent'} buttonName={t('common.ok')} onClick={() => setShowSuccessModal(false)} hideCancel>
                <div style={{textAlign: 'center', padding: '8px 0'}}>
                    <div style={{fontSize: '32px', marginBottom: '12px'}}>✓</div>
                    <div style={{color: '#334155', fontWeight: '600', marginBottom: '4px'}}>{t('settings.users.inviteSentTitle')}</div>
                    <div style={{color: '#94A3B8', fontSize: '14px'}}>{t('settings.users.inviteSentBody')}</div>
                </div>
            </Modal>

            <Modal show={showModal} setter={(v) => { setShowModal(v); setInviteError(''); setSelectedProjectIds([]); }} name={t('settings.users.inviteUser')} buttonName={t('settings.users.sendInvite')} onClick={sendInvite}>
                <TextInput name={'email'} form={form} setForm={setForm} placeholder={'Email'}/>
                <div className={'mb-3'}>
                    <SelectInput name={'role'} form={form} setForm={setForm} options={roles}/>
                </div>

                {availableCompanies.length > 0 && (
                    <div className={'mb-3'}>
                        <div style={{color: '#334155', fontWeight: '600', fontSize: '13px', marginBottom: '4px'}}>{t('settings.users.officeAccess')}</div>
                        <div style={{color: '#64748B', fontSize: '12px', marginBottom: '8px'}}>{t('settings.users.officeAccessHint')}</div>
                        <CompanyOfficeSelector
                            companies={availableCompanies}
                            selectedProjectIds={selectedProjectIds}
                            setSelectedProjectIds={setSelectedProjectIds}
                        />
                    </div>
                )}

                {inviteError && (
                    <div style={{color: '#EF4444', fontSize: '13px', marginTop: '4px'}}>{inviteError}</div>
                )}
            </Modal>

            <div className={styles.dashboardInnerContainer}>
                <SettingsSubmenu active={1}/>

                <SettingsSection title={t('settings.users.title')} description={t('settings.users.description')}>
                    <div side={'left'}>
                        <div className={'d-flex flex-row align-items-center'} onClick={() => setShowModal(true)}>
                            <div className={styles.lightButton}>{t('settings.users.inviteUser')}</div>
                        </div>
                    </div>
                    <div side={'right'} className={'d-flex flex-column'} style={{gap: '16px'}}>
                        <input
                            type={'text'}
                            placeholder={t('settings.users.searchPlaceholder')}
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%', padding: '8px 12px', borderRadius: '8px',
                                border: '1px solid #E2E8F0', fontSize: '14px', color: '#334155',
                                outline: 'none', background: 'white',
                            }}
                        />
                        <div className={`${styles.squareContainer} p-3`}>
                            {users.filter(u => {
                                if (!searchQuery) return true;
                                const q = searchQuery.toLowerCase();
                                const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ').toLowerCase();
                                return fullName.includes(q) ||
                                    (u.username || '').toLowerCase().includes(q) ||
                                    (u.email || '').toLowerCase().includes(q);
                            }).map((user, index, arr) => (
                                <div key={index} className={'d-flex flex-row align-items-center'}
                                     style={{
                                         padding: '12px 8px',
                                         borderBottom: index < arr.length - 1 ? '1px solid #F1F5F9' : 'none',
                                         gap: '12px',
                                     }}>
                                    <img
                                        src={(user?.profile_picture && user?.profile_picture?.image) ? user.profile_picture.image : '/default_pp.png'}
                                        style={{borderRadius: '50%', objectFit: 'cover', flexShrink: 0}}
                                        height={'40px'} width={'40px'} alt={'pp'}
                                    />
                                    <div style={{flex: 1, minWidth: 0}}>
                                        <div style={{color: '#334155', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                            {(user.first_name || user.last_name) ? [user.first_name, user.last_name].filter(Boolean).join(' ') : user.username}
                                        </div>
                                        {(user.first_name || user.last_name) && (
                                            <div style={{color: '#64748B', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{user.username}</div>
                                        )}
                                        <div style={{color: '#94A3B8', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{user.email}</div>
                                        <div style={{color: '#64748B', fontSize: '12px', marginTop: '2px'}}>
                                            <span style={{color: '#94A3B8', fontSize: '11px', textTransform: 'uppercase', fontWeight: '600'}}>{t('settings.users.role')}:</span> {user.role.name}
                                        </div>
                                    </div>
                                    <div className={`${styles.editButton}`} style={{flexShrink: 0}} onClick={() => {
                                        setSelectedUser(user);
                                        setUserForm({
                                            username: user.username || '',
                                            first_name: user.first_name || '',
                                            last_name: user.last_name || '',
                                            role_mod: user.role.id,
                                        });
                                        setEditProjectIds((user.allowed_projects || []).map(p => p.id));
                                        setShowUserModal(true);
                                    }}/>
                                </div>
                            ))}
                        </div>

                        {invites.length > 0 && (
                            <div className={`${styles.squareContainer} p-3`}>
                                <div style={{color: '#94A3B8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '12px'}}>
                                    {t('settings.users.pendingInvitations')}
                                </div>
                                {invites.map((invite, index) => (
                                    <div key={invite.id}
                                         style={{
                                             padding: '10px 8px',
                                             borderBottom: index < invites.length - 1 ? '1px solid #F1F5F9' : 'none',
                                             display: 'flex', alignItems: 'flex-start', gap: '12px',
                                         }}>
                                        <div style={{
                                            width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
                                            background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: '#94A3B8', fontSize: '18px', fontWeight: '600',
                                        }}>
                                            ?
                                        </div>
                                        <div style={{flex: 1, minWidth: 0}}>
                                            <div style={{color: '#334155', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{invite.email}</div>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', flexWrap: 'wrap'}}>
                                                <span style={{background: '#FEF9C3', color: '#A16207', fontSize: '11px', fontWeight: '600', padding: '1px 8px', borderRadius: '20px'}}>
                                                    {t('settings.users.pending')}
                                                </span>
                                                {invite.role?.name && (
                                                    <span style={{color: '#64748B', fontSize: '12px'}}>
                                                        <span style={{color: '#94A3B8', fontSize: '11px', textTransform: 'uppercase', fontWeight: '600'}}>{t('settings.users.role')}:</span> {invite.role.name}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap'}}>
                                                <div
                                                    style={{cursor: 'pointer', color: '#4F46E5', fontSize: '13px', fontWeight: '500', padding: '4px 10px', border: '1px solid #C7D2FE', borderRadius: '6px'}}
                                                    onClick={() => resendInvite(invite.id)}
                                                >
                                                    {t('settings.users.resend')}
                                                </div>
                                                <div
                                                    style={{cursor: 'pointer', color: '#EF4444', fontSize: '13px', fontWeight: '500', padding: '4px 10px', border: '1px solid #FCA5A5', borderRadius: '6px'}}
                                                    onClick={() => cancelInvite(invite.id)}
                                                >
                                                    {t('common.cancel')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </SettingsSection>
            </div>
        </div>
    );
}